/**
 * cart-toast.js — add-to-cart feedback layer.
 *
 * 1. Toast notifications: "added to cart" on ag:cart:added / cart:updated /
 *    cart:refresh, error toast on ag:cart:error.
 * 2. Keeps [data-cart-count] in sync after any add signal.
 * 3. Global fallback interceptor: any form posting to /cart/add that no other
 *    handler owns is submitted via fetch instead of a full page reload.
 *    On product templates, forms with [data-add-to-cart] belong to theme.js
 *    and are left alone; on all other page types (home, collection, search)
 *    live theme.js never binds them, so this layer owns every /cart/add form.
 *    Submit buttons named "checkout" are intentional Buy-Now navigation and
 *    are always left alone.
 */
(function () {
  var style = document.createElement('style');
  style.textContent = [
    '#ag-toast {',
    '  position: fixed;',
    '  bottom: 2rem;',
    '  left: 50%;',
    '  transform: translateX(-50%) translateY(0.5rem);',
    '  background: #0e3e29;',
    '  color: #fff;',
    '  padding: 0.875rem 1.5rem;',
    '  border-radius: 10px;',
    '  font-family: var(--ag-font-ui, system-ui, sans-serif);',
    '  font-size: 0.9375rem;',
    '  font-weight: 500;',
    '  letter-spacing: 0.01em;',
    '  white-space: nowrap;',
    '  box-shadow: 0 8px 24px rgba(0,0,0,0.22);',
    '  opacity: 0;',
    '  transition: opacity 0.22s ease, transform 0.22s ease;',
    '  pointer-events: none;',
    '  z-index: 9999;',
    '}',
    '#ag-toast.is-visible {',
    '  opacity: 1;',
    '  transform: translateX(-50%) translateY(0);',
    '}',
    '#ag-toast.is-error {',
    '  background: #7a2e2e;',
    '  white-space: normal;',
    '  max-width: min(90vw, 26rem);',
    '  text-align: center;',
    '}'
  ].join('\n');
  document.head.appendChild(style);

  var toast = document.createElement('div');
  toast.id = 'ag-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  document.body.appendChild(toast);

  var timer = null;

  function show(message, isError) {
    toast.textContent = message;
    toast.classList.toggle('is-error', !!isError);
    clearTimeout(timer);
    // next frame so a repeated toast re-animates
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });
    timer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 3000);
  }

  document.addEventListener('ag:cart:added', function (e) {
    var title = e.detail && e.detail.title ? e.detail.title : 'Item';
    show(title + ' added to cart \u2713');
  });
  // hero (rice-pdp.js) and homepage cinema signal success through these events
  document.addEventListener('cart:updated', function () { show('Added to cart \u2713'); });
  document.addEventListener('cart:refresh', function () { show('Added to cart \u2713'); });
  document.addEventListener('ag:cart:error', function (e) {
    var msg = e.detail && e.detail.message ? e.detail.message : 'Could not add to cart \u2014 please try again';
    show(msg, true);
  });

  function refreshCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (node) {
          var text = node.textContent;
          if (/^\(\d+\)$/.test(text)) node.textContent = '(' + cart.item_count + ')';
          else if (/\d/.test(text)) node.textContent = text.replace(/\d+/, cart.item_count);
          else node.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }
  ['ag:cart:added', 'ag:cart:updated', 'cart:updated', 'cart:refresh'].forEach(function (evt) {
    document.addEventListener(evt, refreshCount);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = form.getAttribute('action') || '';
    if (!/\/cart\/add(\?|$)/.test(action)) return;
    if (event.defaultPrevented) return;                       // another handler owns it
    // theme.js only binds [data-add-to-cart] forms on product templates
    // (its init gates on template-product) — everywhere else those forms
    // native-POST and reload the page, so this layer owns them there.
    var onProductTemplate = document.body && /(^|\s)template-product(\s|$)/.test(document.body.className);
    if (onProductTemplate && form.querySelector('[data-add-to-cart]')) return;
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') return;   // Buy Now → checkout

    var formData = new FormData(form);
    var id = formData.get('id');
    if (!id) return;

    event.preventDefault();
    var data = {
      id: /^\d+$/.test(id) ? parseInt(id, 10) : id,
      quantity: parseInt(formData.get('quantity'), 10) || 1
    };
    var properties = {};
    formData.forEach(function (value, key) {
      if (key.indexOf('properties[') === 0) {
        var propName = key.replace('properties[', '').replace(']', '');
        if (value) properties[propName] = value;
      }
    });
    if (Object.keys(properties).length > 0) data.properties = properties;

    var btn = submitter || form.querySelector('[type="submit"]');
    var originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(data)
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) {
            var msg = (body && (body.description || body.message)) || 'Could not add to cart \u2014 please try again';
            var err = new Error(msg);
            err.handledMessage = typeof msg === 'string' ? msg : 'Could not add to cart \u2014 please try again';
            throw err;
          }
          return body;
        });
      })
      .then(function (item) {
        document.dispatchEvent(new CustomEvent('ag:cart:added', { detail: { title: item && item.title } }));
        return fetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('ag:cart:updated', { detail: cart }));
            document.dispatchEvent(new CustomEvent('ag:cart:refresh'));
          });
      })
      .catch(function (err) {
        document.dispatchEvent(new CustomEvent('ag:cart:error', {
          detail: { message: err && err.handledMessage }
        }));
      })
      .finally(function () {
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalText;
        }
      });
  });
})();
